/**
 * Build-time configuration.
 *
 * Everything here ships to the browser, so it holds URLs and nothing else. The
 * admin panel has no secrets of its own: the staff session is an httpOnly
 * cookie the browser holds and this code can never read.
 */

function stripTrailingSlash(value: string): string {
  return value.endsWith('/') ? value.slice(0, -1) : value;
}

/**
 * Base URL of the API, prefix included and without a trailing slash.
 *
 * ## THERE IS NO DEFAULT, AND THIS APP IS WHY THE RULE MATTERS MOST
 *
 * It used to fall back to `http://localhost:3001/api` — the **live** API, whose
 * database holds real contacts. `apps/web` carried the identical default and it
 * was removed when a development front-end was found pointing at it; the
 * argument there was that CORS happened to catch it, because the live API
 * allow-lists `:5173` and the development web app was moved to `:5273`.
 *
 * **That accident does not protect this app.** `vite.config.ts` pins `5174` with
 * `strictPort` in every worktree, and the live API allow-lists `5174`. So a
 * staff console started from a development checkout, with no `.env`, binds the
 * port the live API trusts and talks to it — credentialed, allow-listed, working
 * — with nothing on screen saying which database is underneath. That is the
 * staff console, over the live workspace list.
 *
 * So an unset `VITE_API_URL` is a startup failure rather than a guess. Every
 * worktree needs its own `apps/admin/.env`: `http://localhost:3101/api` for
 * development, `http://localhost:3001/api` for the live checkout. It must be
 * UTF-8 — PowerShell's `> file` writes UTF-16, which dotenv reads as no
 * variables at all, so the file looks right and the app behaves as if it is
 * missing.
 */
export const API_BASE_URL: string = (() => {
  const configured = import.meta.env.VITE_API_URL;
  if (configured === undefined || configured.trim() === '') {
    throw new Error(
      'VITE_API_URL is not set. Create apps/admin/.env with VITE_API_URL=http://localhost:3101/api ' +
        '(development) or http://localhost:3001/api (live). There is no default: this app runs on ' +
        'the port the LIVE API allow-lists, so a guess here is a staff console over live data.',
    );
  }
  return stripTrailingSlash(configured.trim());
})();
